// src/components/league/LeagueConstellation.jsx
// League Personality Constellation — two-panel layout:
//   Left:  SVG canvas (X = style, Y = league rank)
//   Right: fixed detail panel (hover or click a node to populate)
//
// Security: all data from internal API, no dangerouslySetInnerHTML.

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ─── Design tokens ────────────────────────────────────────────────────────────
const Q = {
  black:     '#000006',
  blue:      '#3f69ae',
  turquoise: '#44d5a3',
  red:       '#eb5757',
  grey:      '#8a8a8a',
  surface1:  '#1d1d1d',
  surface2:  '#272727',
  border:    '#4a4a4a',
};

// Labels are derived from xNorm (rank percentile) so they are always distributed
// across the full range regardless of how tightly raw scores cluster.
const getRiskLabel = (xNorm) => {
  if (xNorm >= 0.80) return 'Maverick';
  if (xNorm >= 0.60) return 'Calculated Risk-Taker';
  if (xNorm >= 0.40) return 'Balanced Operator';
  if (xNorm >= 0.20) return 'Cautious Planner';
  return 'Template Follower';
};

// ─── Main component ───────────────────────────────────────────────────────────
const LeagueConstellation = () => {
  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();

  const svgContainerRef = useRef(null);
  const [svgSize, setSvgSize] = useState({ w: 600, h: 580 });

  // Active node: whichever was most recently hovered or clicked
  const [hoveredId,  setHoveredId]  = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Measure SVG container ────────────────────────────────────────────────────
  useEffect(() => {
    if (!svgContainerRef.current) return;
    const measure = () => {
      const r = svgContainerRef.current.getBoundingClientRect();
      if (r.width > 0) setSvgSize({ w: r.width, h: r.height });
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(svgContainerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Build manager nodes ──────────────────────────────────────────────────────
  const managers = useMemo(() => {
    if (!data?.weeklyPoints?.length) return [];

    const diffMap  = new Map((data?.differentialPlayers?.summary ?? []).map(r => [r.Manager, r]));
    const formMap  = new Map((data?.formations                   ?? []).map(r => [r.Manager, r]));
    const benchMap = new Map((data?.benchPoints                  ?? []).map(r => [r.Manager, r]));

    return data.weeklyPoints
      .filter(r => r.Total !== undefined)
      .sort((a, b) => (Number(b.Total) || 0) - (Number(a.Total) || 0))
      .map((row, rankIndex, arr) => {
        const diff  = diffMap.get(row.Manager)  ?? {};
        const form  = formMap.get(row.Manager)  ?? {};
        const bench = benchMap.get(row.Manager) ?? {};
        return {
          id:            row.Manager,
          totalPoints:   Number(row.Total)               || 0,
          diffCount:     Number(diff.Differential_Count) || 0,
          formVariety:   Number(form.Formation_Variety)  || 0,
          transferCost:  Number(row.Total_Transfer_Cost) || 0,
          pointsOnBench: Number(bench.Points_on_Bench)   || 0,
          leagueRank:    rankIndex + 1,
          totalManagers: arr.length,
        };
      });
  }, [data]);

  // ── Normalise and enrich ─────────────────────────────────────────────────────
  const enrichedNodes = useMemo(() => {
    if (!managers.length) return [];

    const maxDiff  = Math.max(...managers.map(m => m.diffCount))    || 1;
    const maxForm  = Math.max(...managers.map(m => m.formVariety))  || 1;
    const maxCost  = Math.max(...managers.map(m => m.transferCost)) || 1;
    const pts      = managers.map(m => m.totalPoints);
    const minPts   = Math.min(...pts);
    const ptRange  = (Math.max(...pts) - minPts) || 1;
    const n        = managers.length;

    // Pre-compute risk scores so we can rank-order them for even horizontal spread.
    // Using raw risk score → xNorm causes bunching when scores cluster in a narrow range.
    // Risk signals: differential count (strongest), transfer hits taken, formation variety.
    // Transfer cost was previously inverted (1 - cost) which made template followers score
    // as high-risk. Fixed: more hits = more risk.
    const maxDiffAll  = Math.max(...managers.map(x => x.diffCount))    || 1;
    const maxFormAll  = Math.max(...managers.map(x => x.formVariety))  || 1;
    const maxCostAll  = Math.max(...managers.map(x => x.transferCost)) || 1;
    const riskScores = managers.map(m => {
      const normDiff    = m.diffCount    / maxDiffAll;
      const normCost    = m.transferCost / maxCostAll;  // more hits = higher risk (was inverted)
      const normFormVar = m.formVariety  / maxFormAll;
      return Math.min(1, Math.max(0, normDiff * 0.55 + normCost * 0.30 + normFormVar * 0.15));
    });
    const sortedRiskIndices = [...riskScores.map((s, i) => ({ s, i }))]
      .sort((a, b) => a.s - b.s)
      .map((x, rank) => ({ i: x.i, xNorm: rank / Math.max(managers.length - 1, 1) }));
    const xNormByIndex = new Array(managers.length);
    sortedRiskIndices.forEach(({ i, xNorm }) => { xNormByIndex[i] = xNorm; });

    return managers.map((m, rankIndex, arr) => {
      const normDiff    = m.diffCount    / maxDiff;
      const normFormVar = m.formVariety  / maxForm;
      const normCost    = m.transferCost / maxCost;

      const riskScore = riskScores[rankIndex];

      // Square-root scaling keeps small-point managers visible
      const ptNorm  = (m.totalPoints - minPts) / ptRange;
      // Radius range: 14–34px — bigger nodes, more breathing room
      const radius  = Math.round(14 + 20 * Math.sqrt(ptNorm));

      // xNorm = risk rank position (0 = most template, 1 = biggest risk taker)
      // Using rank rather than raw score guarantees one manager per horizontal percentile.
      // yNorm = rank position: 0 = best (top), 1 = worst (bottom)
      const xNorm = xNormByIndex[rankIndex];

      // Colour interpolates blue (#3f69ae) → orange (#f5a623) along the risk axis (left→right)
      const r = Math.round(0x3f + (0xf5 - 0x3f) * xNorm);
      const g = Math.round(0x69 + (0xa6 - 0x69) * xNorm);
      const b = Math.round(0xae + (0x23 - 0xae) * xNorm);
      const colour = `rgb(${r},${g},${b})`;
      const yNorm = (m.leagueRank - 1) / Math.max(n - 1, 1);

      return {
        ...m,
        normDiff, normFormVar, normCost,
        riskScore, riskLabel: getRiskLabel(xNorm),
        radius, colour,
        initial: (m.id ?? '?').charAt(0).toUpperCase(),
        xNorm, yNorm,
      };
    });
  }, [managers]);

  // ── Position on canvas with overlap-resolution ───────────────────────────────
  const positionedNodes = useMemo(() => {
    if (!enrichedNodes.length || !svgSize.w) return [];

    const PAD = 56;
    const w   = svgSize.w - PAD * 2;
    const h   = svgSize.h - PAD * 2;

    // Initial placement on the two axes
    const nodes = enrichedNodes.map(n => ({
      ...n,
      x: PAD + n.xNorm * w,
      y: PAD + n.yNorm * h,
    }));

    // Iterative repulsion — more iterations, stronger push
    for (let iter = 0; iter < 80; iter++) {
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx   = nodes[j].x - nodes[i].x;
          const dy   = nodes[j].y - nodes[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;
          // Gap between circle edges + 14px buffer — wider spacing
          const min  = nodes[i].radius + nodes[j].radius + 14;
          if (dist < min) {
            const push = (min - dist) / dist * 0.5;
            nodes[i].x -= dx * push;
            nodes[i].y -= dy * push;
            nodes[j].x += dx * push;
            nodes[j].y += dy * push;
          }
        }
      }
    }

    // Clamp within padded canvas
    nodes.forEach(n => {
      n.x = Math.max(PAD, Math.min(svgSize.w - PAD, n.x));
      n.y = Math.max(PAD, Math.min(svgSize.h - PAD, n.y));
    });

    return nodes;
  }, [enrichedNodes, svgSize]);

  // ── Background edges: every node to every other node (very faint) ───────────
  const allEdges = useMemo(() => {
    if (positionedNodes.length < 2) return [];
    const result = [];
    for (let i = 0; i < positionedNodes.length; i++) {
      for (let j = i + 1; j < positionedNodes.length; j++) {
        result.push({ sourceId: positionedNodes[i].id, targetId: positionedNodes[j].id });
      }
    }
    return result;
  }, [positionedNodes]);

  // ── Style-similarity edges (mutual kNN) ──────────────────────────────────────
  // Weighted Euclidean distance matching the risk score formula weights.
  // Uses MUTUAL k-nearest neighbours (k=4): edge (A,B) only forms when B is in
  // A's top-4 AND A is in B's top-4. This prevents hub nodes accumulating many
  // connections simply because they sit near the centre of feature space — every
  // edge represents genuine bidirectional stylistic affinity.
  // Distance cap (0.35) drops pairs that are nearest-neighbours only because
  // the league has no one more similar — not because they actually play alike.
  const edges = useMemo(() => {
    if (positionedNodes.length < 2) return [];
    const MAX_EDGE_DIST = 0.35;
    const K = 4;
    const weightedDist = (a, b) => Math.sqrt(
      (0.55 * (a.normDiff    - b.normDiff))    ** 2 +
      (0.30 * (a.normCost    - b.normCost))    ** 2 +
      (0.15 * (a.normFormVar - b.normFormVar)) ** 2,
    );

    // Build each node's top-K neighbour set (within distance cap)
    const topK = new Map();
    positionedNodes.forEach(node => {
      const neighbours = positionedNodes
        .filter(n => n.id !== node.id)
        .map(n => ({ id: n.id, d: weightedDist(node, n) }))
        .sort((a, b) => a.d - b.d)
        .slice(0, K)
        .filter(({ d }) => d <= MAX_EDGE_DIST)
        .map(({ id }) => id);
      topK.set(node.id, new Set(neighbours));
    });

    // Emit edge only when nomination is mutual
    const edgeSet = new Set();
    const result  = [];
    positionedNodes.forEach(node => {
      topK.get(node.id).forEach(neighbourId => {
        if (topK.get(neighbourId)?.has(node.id)) {
          const key = [node.id, neighbourId].sort().join('||');
          if (!edgeSet.has(key)) {
            edgeSet.add(key);
            result.push({ sourceId: node.id, targetId: neighbourId });
          }
        }
      });
    });
    return result;
  }, [positionedNodes]);

  // ── Search: highlight manager whose name matches the query ──────────────────
  const searchMatchId = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.trim().toLowerCase();
    const match = positionedNodes.find(n => n.id.toLowerCase().includes(q));
    return match ? match.id : null;
  }, [searchQuery, positionedNodes]);

  // The node shown in the right panel: click > hover > search match
  const activeId = selectedId ?? hoveredId ?? searchMatchId;

  // ── Connected nodes for edge highlighting ────────────────────────────────────
  const connectedIds = useMemo(() => {
    if (!activeId) return new Set();
    const s = new Set([activeId]);
    edges.forEach(e => {
      if (e.sourceId === activeId) s.add(e.targetId);
      if (e.targetId === activeId) s.add(e.sourceId);
    });
    return s;
  }, [activeId, edges]);

  const activeNode = useMemo(
    () => positionedNodes.find(n => n.id === activeId) ?? null,
    [positionedNodes, activeId],
  );

  const similarNames = useMemo(() => {
    if (!activeId) return [];
    return edges
      .filter(e => e.sourceId === activeId || e.targetId === activeId)
      .map(e => (e.sourceId === activeId ? e.targetId : e.sourceId))
      .slice(0, 2);
  }, [activeId, edges]);

  const handleNodeClick = useCallback((id) => {
    setSelectedId(prev => prev === id ? null : id);
  }, []);

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!data?.weeklyPoints?.length || !enrichedNodes.length) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '480px', backgroundColor: Q.black, color: Q.grey,
        fontFamily: 'Roboto, sans-serif', fontSize: '0.9rem', textAlign: 'center', padding: '2rem',
      }}>
        No league data available.
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex',
      height: '700px',
      backgroundColor: Q.black,
      fontFamily: 'Roboto, sans-serif',
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${Q.border}`,
    }}>

      {/* ── Left: SVG graph ─────────────────────────────────────────────────── */}
      <div
        ref={svgContainerRef}
        style={{
          flex: 1,
          position: 'relative',
          minWidth: 0,
          backgroundImage: 'radial-gradient(circle, rgba(63,105,174,0.12) 1.5px, transparent 1.5px)',
          backgroundSize: '26px 26px',
        }}
        onClick={() => setSelectedId(null)}
      >
        {/* X-axis labels */}
        <div style={{
          position: 'absolute', top: 6, left: 0, right: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0 12px', pointerEvents: 'none', zIndex: 2,
        }}>
          <span style={{ fontSize: '0.6rem', color: Q.grey }}>← Template Player</span>
          <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.06em' }}>PLAYING STYLE</span>
          <span style={{ fontSize: '0.6rem', color: Q.grey }}>Risk Taker →</span>
        </div>
        {/* Y-axis labels */}
        <div style={{
          position: 'absolute', left: 4, top: 28,
          fontSize: '0.56rem', color: Q.grey, pointerEvents: 'none', zIndex: 2,
          writing: 'vertical-rl',
        }}>
          ↑ 1st
        </div>
        <div style={{
          position: 'absolute', left: 4, bottom: 10,
          fontSize: '0.56rem', color: Q.grey, pointerEvents: 'none', zIndex: 2,
        }}>
          ↓ Last
        </div>

        {positionedNodes.length > 0 && (
          <svg
            width={svgSize.w} height={svgSize.h}
            style={{ display: 'block', position: 'absolute', inset: 0 }}
            aria-label="League constellation graph"
          >
            {/* ── Background graph edges (all pairs, very faint) ────────────── */}
            <g opacity={activeId ? 0 : 0.09} style={{ transition: 'opacity 0.25s' }}>
              {allEdges.map(edge => {
                const src = positionedNodes.find(n => n.id === edge.sourceId);
                const tgt = positionedNodes.find(n => n.id === edge.targetId);
                if (!src || !tgt) return null;
                return (
                  <line
                    key={`bg-${edge.sourceId}||${edge.targetId}`}
                    x1={src.x} y1={src.y} x2={tgt.x} y2={tgt.y}
                    stroke="rgba(255,255,255,0.8)"
                    strokeWidth={0.5}
                    strokeDasharray="2 5"
                  />
                );
              })}
            </g>

            {/* ── Edges ─────────────────────────────────────────────────────── */}
            <g>
              {edges.map(edge => {
                const src = positionedNodes.find(n => n.id === edge.sourceId);
                const tgt = positionedNodes.find(n => n.id === edge.targetId);
                if (!src || !tgt) return null;
                const isHighlighted = activeId && (edge.sourceId === activeId || edge.targetId === activeId);
                const isFaded       = activeId && !isHighlighted;
                return (
                  <line
                    key={`${edge.sourceId}||${edge.targetId}`}
                    x1={src.x} y1={src.y} x2={tgt.x} y2={tgt.y}
                    stroke={isHighlighted ? src.colour : 'rgba(255,255,255,0.22)'}
                    strokeWidth={isHighlighted ? 1.8 : 0.8}
                    strokeOpacity={isFaded ? 0.05 : isHighlighted ? 0.8 : 0.4}
                    strokeDasharray={isHighlighted ? '' : '3 4'}
                    style={{ transition: 'stroke-opacity 0.2s, stroke-width 0.2s' }}
                  />
                );
              })}
            </g>

            {/* ── Nodes ─────────────────────────────────────────────────────── */}
            {positionedNodes.map((node) => {
              const isActive  = node.id === activeId;
              const isYou     = node.id === selectedManager;
              const isDimmed  = (!!activeId && !connectedIds.has(node.id)) ||
                                (!!searchQuery.trim() && node.id !== searchMatchId);

              return (
                <g
                  key={node.id}
                  style={{
                    cursor: 'pointer',
                    opacity: isDimmed ? 0.18 : 1,
                    transition: 'opacity 0.2s',
                  }}
                  onClick={e => { e.stopPropagation(); handleNodeClick(node.id); }}
                  onMouseEnter={() => setHoveredId(node.id)}
                  onMouseLeave={() => setHoveredId(null)}
                >
                  {/* Aura — only shown when active/you */}
                  {(isActive || isYou) && (
                    <circle
                      cx={node.x} cy={node.y} r={node.radius + 7}
                      fill="transparent"
                      stroke={isYou ? Q.turquoise : '#fff'}
                      strokeWidth={1.5}
                      strokeOpacity={0.6}
                    />
                  )}

                  {/* Soft glow behind node */}
                  <circle
                    cx={node.x} cy={node.y} r={node.radius + 4}
                    fill={`${node.colour}20`}
                  />

                  {/* Main node */}
                  <circle
                    cx={node.x} cy={node.y} r={node.radius}
                    fill={node.colour}
                    style={{
                      filter: `drop-shadow(0 0 ${isActive ? 8 : 3}px ${node.colour}88)`,
                      transition: 'filter 0.15s',
                    }}
                  />

                  {/* Highlight shine */}
                  <circle
                    cx={node.x - node.radius * 0.26} cy={node.y - node.radius * 0.26}
                    r={node.radius * 0.36}
                    fill="rgba(255,255,255,0.14)"
                  />

                  {/* Rank badge */}
                  <circle
                    cx={node.x - node.radius * 0.7} cy={node.y - node.radius * 0.7}
                    r={7.5}
                    fill={Q.surface1}
                    stroke={node.colour} strokeWidth={1.2}
                  />
                  <text
                    x={node.x - node.radius * 0.7} y={node.y - node.radius * 0.7}
                    textAnchor="middle" dominantBaseline="central"
                    fill="#fff" fontSize={6} fontWeight={700} fontFamily="Roboto, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {node.leagueRank}
                  </text>

                  {/* Initial */}
                  <text
                    x={node.x} y={node.y}
                    textAnchor="middle" dominantBaseline="central"
                    fill="#fff"
                    fontSize={Math.round(node.radius * 0.7)}
                    fontWeight={700} fontFamily="Roboto, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    {node.initial}
                  </text>

                  {/* Name label — only when active or not dimmed */}
                  {!isDimmed && (
                    <text
                      x={node.x} y={node.y + node.radius + 11}
                      textAnchor="middle" dominantBaseline="hanging"
                      fill={isActive || isYou ? '#fff' : Q.grey}
                      fontSize={8}
                      fontWeight={isActive ? 600 : 400}
                      fontFamily="Roboto, sans-serif"
                      style={{ pointerEvents: 'none', userSelect: 'none', transition: 'fill 0.15s' }}
                    >
                      {node.id.length > 12 ? `${node.id.slice(0, 11)}…` : node.id}
                    </text>
                  )}

                  {/* YOU badge */}
                  {isYou && (
                    <>
                      <rect
                        x={node.x + node.radius - 1} y={node.y - node.radius - 14}
                        width={26} height={12} rx={6}
                        fill={Q.turquoise}
                      />
                      <text
                        x={node.x + node.radius + 12} y={node.y - node.radius - 8}
                        textAnchor="middle" dominantBaseline="central"
                        fill={Q.black} fontSize={7} fontWeight={800}
                        fontFamily="Roboto, sans-serif"
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                      >
                        YOU
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      {/* ── Right: detail panel ──────────────────────────────────────────────── */}
      <div style={{
        width: '260px',
        flexShrink: 0,
        borderLeft: `1px solid ${Q.border}`,
        backgroundColor: Q.surface1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Panel header */}
        <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${Q.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>
            League Constellation
          </div>
          <div style={{ fontSize: '0.66rem', color: Q.grey }}>
            {selectedId ? 'Locked on manager' : hoveredId ? 'Hovering' : searchMatchId ? 'Search match' : 'Hover or click a manager'}
          </div>
          <input
            type="text"
            placeholder="Search manager…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              marginTop: '8px',
              width: '100%',
              backgroundColor: Q.surface2,
              border: `1px solid ${Q.border}`,
              borderRadius: '6px',
              padding: '5px 8px',
              fontSize: '0.7rem',
              color: '#fff',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Legend */}
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${Q.border}`, flexShrink: 0, overflowY: 'auto', maxHeight: '240px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '48px', height: '4px', borderRadius: '2px', background: `linear-gradient(to right, ${Q.blue}, #f5a623)`, opacity: 0.7, flexShrink: 0 }} />
              <span style={{ fontSize: '0.6rem', color: Q.grey }}>Left = template · Right = risk taker</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: Q.grey, opacity: 0.4 }} />
                <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: Q.grey, opacity: 0.8 }} />
              </div>
              <span style={{ fontSize: '0.6rem', color: Q.grey }}>Node size = total points</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '16px', height: '1px', borderTop: '1px dashed rgba(255,255,255,0.35)', flexShrink: 0 }} />
              <span style={{ fontSize: '0.6rem', color: Q.grey }}>Bright lines = similar style</span>
            </div>

          </div>
        </div>

        {/* Manager detail — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
          <AnimatePresence mode="wait">
            {activeNode ? (
              <motion.div
                key={activeNode.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
              >
                {/* Name + rank */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                    backgroundColor: activeNode.colour,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 12px ${activeNode.colour}55`,
                    fontSize: '1rem', fontWeight: 700, color: '#fff',
                  }}>
                    {activeNode.initial}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.85rem', fontWeight: 700, color: '#fff',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '5px',
                    }}>
                      {activeNode.id}
                      {activeNode.id === selectedManager && (
                        <span style={{ fontSize: '0.55rem', fontWeight: 800, color: Q.black, background: Q.turquoise, padding: '1px 5px', borderRadius: '3px', flexShrink: 0 }}>YOU</span>
                      )}
                    </div>
                  </div>
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                    border: `1.5px solid ${activeNode.colour}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '0.52rem', color: Q.grey, lineHeight: 1 }}>RANK</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{activeNode.leagueRank}</span>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                  {[
                    { label: 'Total Points',    value: activeNode.totalPoints.toLocaleString(), accent: Q.turquoise },
                    { label: 'Style Score',     value: `${Math.round(activeNode.riskScore * 100)}%`, accent: activeNode.colour },
                    { label: 'Differentials',   value: activeNode.diffCount,   accent: Q.blue },
                    { label: 'Formations Used', value: activeNode.formVariety, accent: Q.blue },
                    { label: 'Transfer Cost',   value: `-${activeNode.transferCost}`, accent: activeNode.transferCost > 20 ? Q.red : Q.grey },
                    { label: 'Pts on Bench',    value: activeNode.pointsOnBench, accent: Q.grey },
                  ].map(({ label, value, accent }) => (
                    <div key={label} style={{
                      backgroundColor: 'rgba(255,255,255,0.04)',
                      border: `1px solid ${Q.border}`,
                      borderRadius: '8px',
                      padding: '7px 9px',
                    }}>
                      <div style={{ fontSize: '0.58rem', color: Q.grey, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: accent ?? '#fff' }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Style label */}
                <div style={{
                  padding: '8px 10px',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '8px',
                  border: `1px solid ${Q.border}`,
                  marginBottom: '6px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: activeNode.colour, flexShrink: 0 }} />
                    <span style={{ fontSize: '0.72rem', color: '#fff', fontWeight: 600 }}>{activeNode.riskLabel}</span>
                  </div>
                  <p style={{ fontSize: '0.6rem', color: Q.grey, lineHeight: 1.5, margin: 0 }}>
                    Style Score is a blend of: differential picks ({Math.round(activeNode.normDiff * 100)}%), transfer hits taken ({Math.round(activeNode.normCost * 100)}%), and formation variety ({Math.round(activeNode.normFormVar * 100)}%). Higher = more adventurous.
                  </p>
                </div>

                {/* Similar style to */}
                {similarNames.length > 0 && (
                  <div style={{ fontSize: '0.7rem', color: Q.grey, lineHeight: 1.5 }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>Similar style to: </span>
                    {similarNames.map((id, i) => {
                      const n = positionedNodes.find(p => p.id === id);
                      return (
                        <span key={id}>
                          <span
                            style={{ color: n?.colour ?? Q.blue, cursor: 'pointer', fontWeight: 500 }}
                            onClick={() => setSelectedId(id)}
                          >
                            {id}
                          </span>
                          {i < similarNames.length - 1 && ', '}
                        </span>
                      );
                    })}
                  </div>
                )}

                {selectedId && (
                  <button
                    onClick={() => setSelectedId(null)}
                    style={{
                      marginTop: '12px', width: '100%',
                      padding: '6px', borderRadius: '6px',
                      border: `1px solid ${Q.border}`,
                      background: 'transparent', color: Q.grey,
                      fontSize: '0.7rem', cursor: 'pointer',
                    }}
                  >
                    Unlock (show all)
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: '8px',
                  color: Q.grey, textAlign: 'center',
                }}
              >
                <div style={{ fontSize: '1.6rem', opacity: 0.3 }}>✦</div>
                <div style={{ fontSize: '0.75rem' }}>Hover a manager<br />to see their stats</div>
                <div style={{ fontSize: '0.65rem', opacity: 0.6 }}>Click to lock the view</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default LeagueConstellation;
